import { ModuleId, QuestionState } from '../types'
import { ALL_QUESTIONS_CORE } from '../data/questionLoader'

/**
 * Knowledge State Engine - Level 3 Confidence Edition (V5.1)
 * Core philosophy: Simple for basics, metacognition for advanced
 */

/**
 * Update question state - LEVEL 3 CONFIDENCE
 * Level 1 & 2: Simple (correct = master, wrong = investigating)
 * Level 3: Confidence matters (uncertain correct = still investigating)
 */
export function updateQuestionState(
  currentState: QuestionState | undefined,
  questionId: string,
  isCorrect: boolean,
  confidence: number,
  difficulty?: 1 | 2 | 3,
): QuestionState {
  const now = Date.now()

  // Initialize if new question
  if (!currentState) {
    currentState = {
      questionId,
      status: 'unseen',
      attempts: 0,
      correctAttempts: 0,
      lastConfidenceScore: 0,
      lastAttemptDate: 0,
      masteryPoints: 0,
      history: [],
    }
  }

  // Update history
  const newHistory = [
    ...currentState.history,
    { isCorrect, confidence, timestamp: now },
  ].slice(-5)

  const newAttempts = currentState.attempts + 1
  const newCorrectAttempts = currentState.correctAttempts + (isCorrect ? 1 : 0)

  // Status Logic based on difficulty
  let newStatus: QuestionStatus
  let masteryPoints: number

  if (difficulty === 3) {
    // LEVEL 3 (E6): Confidence matters
    if (isCorrect) {
      if (confidence >= 3) {
        // Confident and correct = Master
        newStatus = 'master'
        masteryPoints = 1.0
      } else {
        // Uncertain but correct = Still needs practice
        newStatus = 'investigating'
        masteryPoints = 0
      }
    } else {
      // Wrong = Investigating
      newStatus = 'investigating'
      masteryPoints = 0
    }
  } else {
    // LEVEL 1 & 2 (M6, M6+): Simple
    if (isCorrect) {
      newStatus = 'master'
      masteryPoints = 1.0
    } else {
      newStatus = 'investigating'
      masteryPoints = 0
    }
  }

  return {
    questionId,
    status: newStatus,
    attempts: newAttempts,
    correctAttempts: newCorrectAttempts,
    lastConfidenceScore: confidence,
    lastAttemptDate: now,
    masteryPoints,
    history: newHistory,
  }
}

/**
 * Calculate mastery percentage - SIMPLIFIED (correct/total)
 */
export function calculateMasteryPercentage(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
): number {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId,
  )
  const totalQuestions = moduleQuestions.length

  if (totalQuestions === 0) return 0

  const correctCount = moduleQuestions.reduce((sum, q) => {
    const state = questionStates[q.id]
    return sum + (state?.status === 'master' ? 1 : 0)
  }, 0)

  return Math.round((correctCount / totalQuestions) * 100)
}

/**
 * Get review queue for "Superkracht Training"
 */
export function getReviewQueue(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
): string[] {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId,
  )

  return moduleQuestions
    .filter((q) => {
      const state = questionStates[q.id]
      return state?.status === 'investigating'
    })
    .map((q) => q.id)
}

/**
 * Get questions by status for a module
 */
export function getQuestionsByStatus(
  moduleId: ModuleId,
  status: QuestionStatus,
  questionStates: Record<string, QuestionState>,
): string[] {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId,
  )

  return moduleQuestions
    .filter((q) => {
      const state = questionStates[q.id]
      return state?.status === status || (!state && status === 'unseen')
    })
    .map((q) => q.id)
}

/**
 * Smart question selection
 */
export function selectSmartQuestion(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  recentQuestions: string[] = [],
): string | null {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId,
  )

  const availableQuestions = moduleQuestions.filter(
    (q) => !recentQuestions.includes(q.id),
  )

  if (availableQuestions.length === 0) {
    return moduleQuestions[0]?.id || null
  }

  // Priority 1: Wrong answers (investigating)
  const investigating = availableQuestions.filter((q) => {
    const state = questionStates[q.id]
    return state?.status === 'investigating'
  })
  if (investigating.length > 0) {
    return investigating[Math.floor(Math.random() * investigating.length)].id
  }

  // Priority 2: Unseen (new questions)
  const unseen = availableQuestions.filter((q) => {
    const state = questionStates[q.id]
    return !state || state.status === 'unseen'
  })
  if (unseen.length > 0) {
    return unseen[Math.floor(Math.random() * unseen.length)].id
  }

  // Priority 3: Master (for review/maintenance)
  const master = availableQuestions.filter((q) => {
    const state = questionStates[q.id]
    return state?.status === 'master'
  })
  if (master.length > 0) {
    return master[Math.floor(Math.random() * master.length)].id
  }

  return availableQuestions[0]?.id || null
}

/**
 * Get module statistics
 */
export function getModuleStats(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
) {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId,
  )

  const stats = {
    total: moduleQuestions.length,
    unseen: 0,
    master: 0,
    investigating: 0,
    reviewNeeded: 0,
    // Legacy fields
    exploring: 0,
    developing: 0,
    braveLearning: 0,
    uncertainCount: 0,
    overconfidentCount: 0,
  }

  moduleQuestions.forEach((q) => {
    const state = questionStates[q.id]

    if (!state || state.status === 'unseen') {
      stats.unseen++
    } else if (state.status === 'master') {
      stats.master++
    } else if (state.status === 'investigating') {
      stats.investigating++
      stats.reviewNeeded++
      stats.overconfidentCount++
    }
  })

  return stats
}
